import { Box, Flex, Text } from "@daangn/carotene";
import { atoms } from "@daangn/carotene/css";
import IconChevronLeftLine from "@daangn/react-monochrome-icon/IconChevronLeftLine";
import IconChevronRightLine from "@daangn/react-monochrome-icon/IconChevronRightLine";
import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";
import type { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import * as React from "react";

import { useDisclosure } from "~/hooks/useDisclosure";
import { useDotButton } from "~/hooks/useDotButton";
import { usePrevNextButtons } from "~/hooks/usePrevNextButtons";

import * as css from "./ImageCarousel.css";
import { ImageGalleryDialog } from "./ImageGalleryDialog";
import type { ImageSlide } from "./Lightbox";
import Lightbox from "./Lightbox";
import Match from "./Match";

enum Type {
  Indicator = "Indicator",
  Gallery = "Gallery",
}

type ImageCarouselProps = {
  slides: ImageSlide[];
  clickable?: boolean;
  options?: Omit<EmblaOptionsType, "slides">;
  type?: keyof typeof Type;
  renderCarouselImage?: (slide: ImageSlide) => React.ReactNode;
};
const ImageCarousel: React.FC<ImageCarouselProps> = ({
  slides,
  clickable,
  options,
  type = Type.Indicator,
  renderCarouselImage,
}) => {
  const [open, setToggleLightbox] = React.useReducer((t) => !t, false);
  const {
    isOpen: isImageGalleryDialogOpen,
    onOpen: onImageGalleryOpen,
    setOpenState: setImageGalleryDialogOpenState,
  } = useDisclosure();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...options,
    active: slides.length > 1,
  });

  const {
    selectedIndex: currentIndex,
    scrollSnaps,
    onDotButtonClick,
  } = useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <>
      <div
        className={atoms({
          overflow: "hidden",
          width: "full",
          position: "relative",
        })}
        ref={emblaRef}
        id="relative"
      >
        <Flex width="full">
          {slides.map((slide) => (
            <Slot
              key={slide.src}
              className={css.emblaSlide({
                clickable,
              })}
              onClick={() => {
                if (!clickable) {
                  return;
                }

                setToggleLightbox();
              }}
            >
              {renderCarouselImage ? (
                renderCarouselImage(slide)
              ) : (
                <img src={slide.src} alt={slide.alt ?? ""} loading="lazy" />
              )}
            </Slot>
          ))}
        </Flex>
        <Box display={{ base: "none", medium: "block" }}>
          <button
            type="button"
            aria-label="Previous item"
            onClick={onPrevButtonClick}
            className={css.prevNavButton({
              disabled: prevBtnDisabled,
            })}
          >
            <IconChevronLeftLine size={32} className={css.navButtonIcon} />
          </button>
          <button
            type="button"
            aria-label="Next item"
            onClick={onNextButtonClick}
            className={css.nextNavButton({
              disabled: nextBtnDisabled,
            })}
          >
            <IconChevronRightLine size={32} className={css.navButtonIcon} />
          </button>
        </Box>
        <Match match={type}>
          <Match.Case predicate={Type.Gallery}>
            <ImageGalleryDialogTrigger
              currentIndex={currentIndex}
              length={slides.length}
              onClick={onImageGalleryOpen}
            />
            <ImageGalleryDialog
              slides={slides}
              open={isImageGalleryDialogOpen}
              onOpenChange={setImageGalleryDialogOpenState}
            />
          </Match.Case>
          <Match.Otherwise>
            <ol className={css.pagination}>
              {scrollSnaps.map((_, i) => (
                <li key={i} className={css.paginationItem}>
                  <button
                    type="button"
                    aria-label="Carousel indicator"
                    className={css.paginationButton({
                      active: i === currentIndex,
                    })}
                    onClick={() => onDotButtonClick(i)}
                  />
                </li>
              ))}
            </ol>
          </Match.Otherwise>
        </Match>
      </div>
      {clickable && (
        <Lightbox
          index={currentIndex}
          open={open}
          close={setToggleLightbox}
          slides={slides}
          on={{
            view: ({ index }) => onDotButtonClick(index),
          }}
        />
      )}
    </>
  );
};

export default ImageCarousel;

type ImageGalleryDialogTriggerProps = {
  currentIndex: number;
  length: number;
  onClick: () => void;
};

function ImageGalleryDialogTrigger({
  currentIndex,
  length,
  onClick,
}: ImageGalleryDialogTriggerProps) {
  return (
    <Text color="onSolid" size={{ base: "xsmall", small: "small" }} asChild>
      <Flex
        as="button"
        gap={1.5}
        alignItems="center"
        className={css.imageGalleryTrigger}
        onClick={onClick}
      >
        <span className={css.imageGalleryTriggerHoverPressed}>
          {currentIndex + 1}/{length}
        </span>
        <div
          className={clsx(
            css.verticalDivider,
            css.imageGalleryTriggerHoverPressed,
          )}
        />
        <span className={css.imageGalleryTriggerHoverPressed}>전체보기</span>
      </Flex>
    </Text>
  );
}
